import {MenuDocument} from "features/editor/MenuDocumentModel";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {menuApi} from "lib/api/wineMenuApi";
import {AppDispatch, AppState} from "lib/redux/store";
import { menuEditorActions } from "features/editor/editorSlice";

interface EditorPageDocument {
    content: MenuDocument
    fileId: string
    userId: string
}

interface MenuEditorPageState {
    document: EditorPageDocument | null
    loading: boolean
    error: string | null
}

const initialState: MenuEditorPageState = {
    document: null,
    loading: false,
    error: null
}

export interface FetchDocumentRequest {
    userId: string
    fileId: string
}

const fetchMenuDocument = createAsyncThunk<any, FetchDocumentRequest, {}>(
    "menuEditorPage/fetchDocument",
    async (request: FetchDocumentRequest) => {
        const response = await menuApi.getMenuContents(request);
        return response.data;
    }
);

function fetchMenuDocumentIfNecessary(request: FetchDocumentRequest) {
    return (dispatch: AppDispatch, getState: () => AppState) => {
        const state = getState();
        const currentDocument = state.menu.editorPage.document;
        const requestedDocumentIsSame =
            currentDocument?.fileId === request.fileId &&
            currentDocument?.userId === request.userId
        if (requestedDocumentIsSame) {
            return Promise.resolve(currentDocument.content);
        } else { 
            dispatch(menuEditorActions.resetDocumentState());
            return dispatch(fetchMenuDocument(request));
        }
    }
}

export interface SaveDocumentRequest {
    userId: string
    fileId: string
    name: string
    document: MenuDocument
}

const saveDocument = createAsyncThunk<any, SaveDocumentRequest, {}>(
    "menuEditorPage/saveDocument",
    async (request: SaveDocumentRequest) => {
        const response = await menuApi.replaceMenu({
            userId: request.userId,
            fileId: request.fileId,
            name: request.name,
            body: JSON.stringify(request.document)
        });
        return response.data;
    }
);

const {actions, reducer} = createSlice({
    name: "menuEditorPage",
    initialState: initialState,
    reducers: {
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchMenuDocument.pending, state => {
            state.document = null;
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchMenuDocument.fulfilled, (state, action) => {
           state.document = {
               content: action.payload,
               userId: action.meta.arg.userId,
               fileId: action.meta.arg.fileId
           };
           state.loading = false;
           state.error = null;
        });
        builder.addCase(fetchMenuDocument.rejected, (state, action) => {
            state.document = null;
            state.loading = false;
            state.error = action.error.message ?? null;
        });

        builder.addCase(saveDocument.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(saveDocument.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.document = {
                content: action.meta.arg.document,
                userId: action.meta.arg.userId,
                fileId: action.meta.arg.fileId
            };
        });
        builder.addCase(saveDocument.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message ?? null;
        });
    }
});

function getMenuEditorPageState(state: AppState): MenuEditorPageState {
    return state.menu.editorPage;
}

const selectors = {
    documentContent: (state: AppState) => getMenuEditorPageState(state).document?.content,
    loading: (state: AppState) => getMenuEditorPageState(state).loading,
    error: (state: AppState) => getMenuEditorPageState(state).error
};

const publicActions = {
    setError: actions.setError,
    fetchMenuDocumentIfNecessary,
    saveDocument
};

export {selectors as menuEditorSelectors};
export {publicActions as menuEditorActions};
export {reducer as menuEditorPageReducer};
